import { OrderStatus } from "@ifa/utils";

type OrderProgressLike = {
  orderStatus?: string;
  policyholder?: unknown;
  insured?: unknown;
  isFinancialInfoRequired?: boolean;
  financialCompleted?: boolean;
  fnaCompleted?: boolean;
  productCompleted?: boolean;
  attachmentCompleted?: boolean;
};

export type FinancialSubStep = "financial-info" | "fna";

type OrderProgressOptions = {
  isCorrectionMode?: boolean;
  tenantCode?: string | null;
};

function isFinancialInfoRequiredForProgress(
  data?: OrderProgressLike | null,
  options?: OrderProgressOptions,
) {
  return options?.tenantCode === "zuu" ? true : data?.isFinancialInfoRequired;
}

export function isOrderFinancialStepComplete(
  data?: OrderProgressLike | null,
  options?: OrderProgressOptions,
) {
  if (!data) return false;
  const isFinancialInfoRequired = isFinancialInfoRequiredForProgress(
    data,
    options,
  );
  return Boolean(data.financialCompleted) && (!isFinancialInfoRequired || Boolean(data.fnaCompleted));
}

export function shouldShowCraStep(orderStatus?: string) {
  return (
    orderStatus === OrderStatus.OrderAudit ||
    orderStatus === OrderStatus.ComplianceAudit ||
    orderStatus === OrderStatus.Completed
  );
}

export function deriveOrderEditTopStep(
  data?: OrderProgressLike | null,
  options?: OrderProgressOptions,
) {
  if (!data) return 0;

  if (data.orderStatus === OrderStatus.Draft) {
    return 0;
  }

  let maxStep = 0;

  if (data.policyholder && data.insured) {
    maxStep = 1;

    if (isOrderFinancialStepComplete(data, options)) {
      maxStep = 2;

      if (data.productCompleted) {
        maxStep = 3;

        if (data.attachmentCompleted) {
          maxStep = 4;
        }
      }
    }
  }

  if (
    data.orderStatus === OrderStatus.OrderAudit ||
    options?.isCorrectionMode
  ) {
    return Math.min(maxStep, 2);
  }

  return maxStep;
}

export function deriveOrderDetailTopLogicalStep(
  data?: OrderProgressLike | null,
  options?: OrderProgressOptions,
) {
  if (!data) return 0;

  if (!data.policyholder || !data.insured) {
    return 0;
  }

  if (!isOrderFinancialStepComplete(data, options)) {
    return 1;
  }

  if (!data.productCompleted) {
    return 2;
  }

  if (!data.attachmentCompleted) {
    return 3;
  }

  if (shouldShowCraStep(data.orderStatus)) {
    return 5;
  }

  return 4;
}

export function deriveOrderEditFinancialSubStep(
  data?: OrderProgressLike | null,
  options?: OrderProgressOptions,
): FinancialSubStep {
  if (!isFinancialInfoRequiredForProgress(data, options)) {
    return "financial-info";
  }

  return data.financialCompleted ? "fna" : "financial-info";
}

export function deriveOrderDetailFinancialSubStep(
  data?: OrderProgressLike | null,
  options?: OrderProgressOptions,
): FinancialSubStep {
  if (!isFinancialInfoRequiredForProgress(data, options)) {
    return "financial-info";
  }

  return data.fnaCompleted ? "fna" : "financial-info";
}
